'use client'
import clsx from 'clsx'
import Image from 'next/image'
import { useState } from 'react'

import { PromotionsEnquireDrawer } from '@/components/common/PromotionsEnquireDrawer/PromotionsEnquireDrawer'
import { Button, ButtonLink } from '@/components/primitives/Button/Button'
import { Heading } from '@/components/primitives/Heading/Heading'
import { CONTENTFUL_ASSETS } from '@/lib/contentful-assets.constants'
import { extractRichTextContent } from '@/lib/utils'

import type { ContentBannerFields } from '@/types/contentful/contentBannerType'

const DESCRIPTION_LIMIT = 100

export const ContentBanner = ({ title, subtitle, size = 'lg', actionButton, enquireFormLink }: ContentBannerFields) => {
  const [showFull, setShowFull] = useState(false)
  const description = extractRichTextContent(subtitle)
  const isLong = description && description.length > DESCRIPTION_LIMIT
  const visibleText = showFull || !isLong ? description : description?.slice(0, DESCRIPTION_LIMIT) + '.'
  const buttonLabel = actionButton?.fields?.label
  const buttonUrl = actionButton?.fields?.url

  const renderCtaButton = () => {
    if (buttonLabel && enquireFormLink) {
      return (
        <PromotionsEnquireDrawer formSrc={enquireFormLink}>
          <Button>{buttonLabel}</Button>
        </PromotionsEnquireDrawer>
      )
    }

    if (buttonLabel && buttonUrl) {
      return <ButtonLink href={buttonUrl}>{buttonLabel}</ButtonLink>
    }

    return null
  }

  const ctaButton = renderCtaButton()

  return (
    <section
      data-name="content-banner"
      className={clsx('relative flex w-full  flex-col items-center justify-center px-8', size == 'lg' ? 'py-24 lg:py-52 xl:py-60' : 'py-[138px] lg:py-[154px]')}
    >
      <Image src={CONTENTFUL_ASSETS.HERO_BACKGROUND} alt="Hero banner" fill sizes="100vw" priority className="z-0 object-cover" />
      <div className="z-10 text-center">
        {title && (
          <Heading as="h2" className="mb-2 text-accent-6 md:mb-3">
            {title}
          </Heading>
        )}
        <p className="max-w-md text-accent-7 content:mx-auto content:max-w-content">
          {visibleText}
          {isLong && (
            <button onClick={() => setShowFull(!showFull)} className="ml-1 text-accent-7 underline">
              {showFull ? 'Read Less' : 'Read More'}
            </button>
          )}
        </p>
        {ctaButton && <div className="mt-6 flex justify-center">{ctaButton}</div>}
      </div>
    </section>
  )
}
